import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import "./PatientInfoDisplay.css";
import { FaMoneyBillWave, FaCalendarAlt, FaArrowLeft, FaBoxOpen, FaUser, FaMapMarkerAlt, FaPhoneAlt, FaIdCard, FaEdit, FaSearchPlus } from "react-icons/fa";
import { toast } from "react-toastify";
import { MdContentCopy } from "react-icons/md";
import EditPatientModal from "./EditPatientModal";
import axios from "axios";
import { API_BASE_URL } from "../../../../Constants/ApiEndpoints";

const PatientInfoDisplay = ({ patient }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('info');
  const [currentPatient, setCurrentPatient] = useState(patient);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [initialRegDate, setInitialRegDate] = useState('');
  const initialFlagged = Boolean(patient?.flagged ?? patient?.isFlagged ?? false);
  const [isFlagged, setIsFlagged] = useState(initialFlagged);
  const [flagLoading, setFlagLoading] = useState(false);

  useEffect(() => {
    const activeFlag = patient?.flagged ?? patient?.isFlagged;
    if (activeFlag !== undefined) {
      setIsFlagged(Boolean(activeFlag));
    }
  }, [patient?.flagged, patient?.isFlagged]);

  const handleFlagClick = async () => {
    const mrNo = currentPatient?.mrNumber || patient?.mrNumber;
    if (!mrNo) return;
    const newFlagValue = !isFlagged;
    setFlagLoading(true);
    try {
      await axios.patch(
        `${API_BASE_URL}/flagThePatient/${mrNo}/${newFlagValue}`
      );
      setIsFlagged(newFlagValue);
      setCurrentPatient(prev => ({ ...prev, flagged: newFlagValue, isFlagged: newFlagValue }));
      if (patient) {
        patient.flagged = newFlagValue;
        patient.isFlagged = newFlagValue;
      }
      toast.success("Patient flag status updated!");
    } catch (err) {
      console.error("Failed to update flag:", err);
      toast.error("Failed to update flag status.");
    } finally {
      setFlagLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPatient(patient);

    const fetchInitialRegDate = async () => {
      if (!patient?.mrNumber) return;

      // 1. Direct date check in patient object
      const directDate = patient.initialRegistrationDate ||
                         patient.registrationDate ||
                         patient.createdDate ||
                         patient.createdTimestamp;

      if (directDate) {
        setInitialRegDate(directDate);
        return;
      }

      // 2. Fetch from backend endpoint: GET /getInitialRegistrationDate/{mrNumber}
      try {
        const response = await axios.get(`${API_BASE_URL}/getInitialRegistrationDate/${patient.mrNumber}`);
        if (response.data) {
          const dateVal = typeof response.data === 'object' ? (response.data.initialRegDate || response.data.createdDate || response.data.registrationDate) : response.data;
          if (dateVal) {
            setInitialRegDate(dateVal);
            return;
          }
        }
      } catch (err) {
        console.warn('Backend endpoint /getInitialRegistrationDate not available, trying earliest appointment date fallback');
      }

      // 3. Fallback: Find earliest appointment date
      const appts = patient.appointments || [];
      if (appts.length > 0) {
        const sortedAppts = [...appts].sort((a, b) => {
          const dateA = new Date(a.appointmentDate || a.date || a.createdDate || 0);
          const dateB = new Date(b.appointmentDate || b.date || b.createdDate || 0);
          return dateA - dateB;
        });
        const earliest = sortedAppts[0];
        const earliestDate = earliest.appointmentDate || earliest.date || earliest.createdDate;
        if (earliestDate) {
          setInitialRegDate(earliestDate);
          return;
        }
      }

      // 4. Default fallback
      setInitialRegDate(patient.appointmentDate || patient.bookingDate || '');
    };

    fetchInitialRegDate();
  }, [patient]);

  const handlePatientUpdated = (updatedData) => {
    setCurrentPatient((prev) => ({
      ...prev,
      ...updatedData,
    }));
  };

  const copyToClipboard = () => {
    if (!currentPatient?.mrNumber) return;
    navigator.clipboard.writeText(currentPatient.mrNumber)
      .then(() => toast.success('MR Number copied to clipboard!'))
      .catch(() => toast.error('Failed to copy MR Number'));
  };

  const formatReadableDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = date.getDate();
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  if (!currentPatient) {
    return (
      <div className="patient-info-display-container">
        <div className="master-patient-profile-card">
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            <p>No patient profile data found.</p>
            <button className="hero-back-btn" onClick={() => navigate("/outpatient/patients")}>
              <FaArrowLeft /> Return to Patients List
            </button>
          </div>
        </div>
      </div>
    );
  }

  const photoSrc = currentPatient.photo
    ? `data:image/jpeg;base64,${currentPatient.photo}`
    : "/VThospitalsIcon.png";

  const appointments = currentPatient.appointments || [];
  const rawInvoices = currentPatient.paymentPackageHistory?.invoices || [];

  // Filter out invoices where paid amount is 0
  const invoices = rawInvoices.filter(inv => {
    const paid = inv.amountPaid !== undefined && inv.amountPaid !== null
      ? inv.amountPaid
      : (inv.paidAmount !== undefined && inv.paidAmount !== null
          ? inv.paidAmount
          : (inv.totalAmount || 0));
    return Number(paid) > 0;
  });

  // Filter package invoices based on invoiceName, invoiceType, or invoiceDescription containing 'package'
  const packageInvoices = invoices.filter((inv) => {
    const name = (inv.invoiceName || '').toLowerCase();
    const type = (inv.invoiceType || '').toUpperCase();
    const desc = (inv.invoiceDescription || '').toLowerCase();
    return name.includes('package') || type === 'PACKAGE' || desc.includes('package');
  });

  const rawPackages = currentPatient.paymentPackageHistory?.packages || [];
  const displayPackages = rawPackages.length > 0 ? rawPackages : packageInvoices;

  const getDoctorName = (str) => {
    if (!str) return "N/A";
    const parts = str.split(" | ");
    if (parts.length < 2) return "N/A";
    const doctorPart = parts[1].split("Dr: ")[1];
    return doctorPart ? doctorPart.trim() : "N/A";
  };

  const getAppointmentId = (str) => {
    if (!str) return "N/A";
    const parts = str.split(" | ");
    return parts[0] ? parts[0].trim() : "N/A";
  };

  return (
    <div className="patient-info-display-container">
      <div className="master-patient-profile-card">
        {/* === Dark Slate Hero Banner Header === */}
        <div className="patient-hero-banner">
          <div className="hero-left-group">
            <div
              className="hero-avatar-ring"
              onClick={() => setShowPhotoModal(true)}
              style={{ position: 'relative', cursor: 'pointer' }}
              title="Click to zoom patient photo"
            >
              <img src={photoSrc} alt={currentPatient.patientName} className="hero-avatar-img" />

              {/* Full Hover Overlay Icon (Exact parity with PatientInfoPanel) */}
              <div className="avatar-hover-overlay">
                <FaSearchPlus size={20} color="#ffffff" />
              </div>
            </div>

            <div className="hero-details">
              <div className="hero-name-row">
                <h2>{currentPatient.patientName}</h2>
                <span className="hero-gender-badge">{currentPatient.gender || 'Male'}</span>
              </div>

              <div className="hero-meta-pills">
                <span className="hero-pill mr-pill-badge">
                  <FaIdCard size={11} /> MR: <strong>{currentPatient.mrNumber}</strong>
                  <button className="hero-copy-btn" onClick={copyToClipboard} title="Copy MR Number">
                    <MdContentCopy size={12} />
                  </button>
                </span>
                <span className="hero-pill">
                  Age: <strong>{currentPatient.age ? `${currentPatient.age} Yrs` : 'N/A'}</strong>
                </span>
                <span className="hero-pill">
                  <FaPhoneAlt size={10} /> <strong>{currentPatient.phoneNumber || 'N/A'}</strong>
                </span>
                <button
                  className="priority-flag-btn"
                  onClick={handleFlagClick}
                  disabled={flagLoading}
                  style={{
                    backgroundColor: isFlagged ? "#dc2626" : "#16a34a",
                  }}
                >
                  {flagLoading ? "Processing..." : isFlagged ? "🚨 Flagged" : "🚨 Flag"}
                </button>
              </div>
            </div>
          </div>

          <div className="hero-action-buttons">
            <button className="hero-edit-btn" onClick={() => setShowEditModal(true)} title="Edit Patient Details">
              <FaEdit size={12} /> Edit Patient
            </button>
            <button className="hero-back-btn" onClick={() => navigate("/outpatient/patients")}>
              <FaArrowLeft size={12} /> Back to Patients
            </button>
          </div>
        </div>

        {/* === Integrated Segmented Tab Navigation === */}
        <div className="profile-tabs-nav">
          <button
            className={`profile-tab-btn ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
          >
            <FaUser size={13} /> General Information
          </button>
          <button
            className={`profile-tab-btn ${activeTab === 'appointments' ? 'active' : ''}`}
            onClick={() => setActiveTab('appointments')}
          >
            <FaCalendarAlt size={13} /> Appointment History ({appointments.length})
          </button>
          <button
            className={`profile-tab-btn ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => setActiveTab('payments')}
          >
            <FaMoneyBillWave size={13} /> Payment History ({invoices.length})
          </button>
          <button
            className={`profile-tab-btn ${activeTab === 'packages' ? 'active' : ''}`}
            onClick={() => setActiveTab('packages')}
          >
            <FaBoxOpen size={13} /> Package History ({displayPackages.length})
          </button>
        </div>

        {/* === Tab Content View Area === */}
        <div className="profile-tab-content">
          {/* TAB 1: General Info */}
          {activeTab === 'info' && (
            <div className="info-grid-layout">
              <div className="info-section-card">
                <div className="section-card-header">
                  <FaUser color="#2563eb" size={14} /> Basic Demographics
                </div>
                <div className="info-row">
                  <span className="info-label">Full Name:</span>
                  <span className="info-value"><strong>{currentPatient.patientName}</strong></span>
                </div>
                <div className="info-row">
                  <span className="info-label">MR Number:</span>
                  <span className="info-value code-value">{currentPatient.mrNumber}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Gender:</span>
                  <span className="info-value">{currentPatient.gender || 'N/A'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Age:</span>
                  <span className="info-value">{currentPatient.age ? `${currentPatient.age} Years` : 'N/A'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Phone Number:</span>
                  <span className="info-value">{currentPatient.phoneNumber || 'N/A'}</span>
                </div>
              </div>

              <div className="info-section-card">
                <div className="section-card-header">
                  <FaMapMarkerAlt color="#2563eb" size={14} /> Address & Registration Details
                </div>
                <div className="info-row">
                  <span className="info-label">Street Address:</span>
                  <span className="info-value">{currentPatient.address || 'N/A'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">City / Location:</span>
                  <span className="info-value">{currentPatient.city || 'N/A'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Initial Reg Date:</span>
                  <span className="info-value">
                    {formatReadableDate(
                      initialRegDate ||
                      currentPatient.initialRegistrationDate ||
                      currentPatient.registrationDate ||
                      currentPatient.createdDate ||
                      currentPatient.appointmentDate
                    )}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Patient Status:</span>
                  <span className={currentPatient.type === 'IN_PATIENT' ? "status-badge-active inpatient-badge" : "status-badge-active"}>
                    {currentPatient.patientStatus || currentPatient.status || (currentPatient.type === 'IN_PATIENT' ? 'ACTIVE INPATIENT' : 'ACTIVE OUTPATIENT')}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: Appointment History */}
          {activeTab === 'appointments' && (
            <div className="embedded-table-wrapper">
              <table className="embedded-master-table">
                <thead>
                  <tr>
                    <th>DATE</th>
                    <th>DOCTOR NAME</th>
                    <th>BOOKING TYPE</th>
                  </tr>
                </thead>
                <tbody>
                  {appointments.length > 0 ? (
                    appointments.map((appt, idx) => (
                      <tr
                        key={idx}
                        className="clickable-table-row"
                        onClick={() => window.open(`/outpatient/appointments/patientInfoPanel/${appt.appointmentId}`, '_blank')}
                        title="Click to view appointment details"
                      >
                        <td><strong>{formatReadableDate(appt.appointmentDate)}</strong></td>
                        <td>{appt.doctorName || 'N/A'}</td>
                        <td>
                          <span className="type-pill">{appt.bookingType || 'WALKIN'}</span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="empty-table-msg">No appointment history records found for this patient.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* TAB 3: Payment History */}
          {activeTab === 'payments' && (
            <div className="embedded-table-wrapper">
              <table className="embedded-master-table">
                <thead>
                  <tr>
                    <th>DATE</th>
                    <th>DOCTOR</th>
                    <th>PARTICULARS</th>
                    <th>AMOUNT (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.length > 0 ? (
                    invoices.map((inv, idx) => {
                      const paidAmount = inv.amountPaid !== undefined && inv.amountPaid !== null
                        ? inv.amountPaid
                        : (inv.paidAmount !== undefined && inv.paidAmount !== null
                            ? inv.paidAmount
                            : (inv.totalAmount || 0));

                      return (
                        <tr
                          key={idx}
                          className="clickable-table-row"
                          onClick={() => window.open(`/outpatient/appointments/patientInfoPanel/${getAppointmentId(inv.appointmentId)}`, '_blank')}
                          title="Click to view invoice details"
                        >
                          <td><strong>{formatReadableDate(inv.invoiceTime)}</strong></td>
                          <td>{getDoctorName(inv.appointmentId)}</td>
                          <td>{inv.invoiceName || 'Consultation Fee'}</td>
                          <td className="amount-cell">₹{paidAmount}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="4" className="empty-table-msg">No payment invoice records found for this patient.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* TAB 4: Package History */}
          {activeTab === 'packages' && (
            displayPackages.length > 0 ? (
              <div className="embedded-table-wrapper">
                <table className="embedded-master-table">
                  <thead>
                    <tr>
                      <th>DATE</th>
                      <th>PACKAGE NAME</th>
                      <th>SESSIONS</th>
                      <th>TOTAL CHARGE (₹)</th>
                      <th>DISCOUNT (₹)</th>
                      <th>PAID AMOUNT (₹)</th>
                      <th>REFUND AMOUNT (₹)</th>
                      <th>STATUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayPackages.map((pkg, idx) => {
                      const rawStatus = pkg.invoiceStatus || pkg.paymentStatus || pkg.status || 'COMPLETED';
                      const st = String(rawStatus).toUpperCase();
                      let statusClass = "status-badge-active";
                      if (st === 'REFUNDED' || st === 'CANCELLED') statusClass = "refund-badge";
                      if (st === 'PENDING' || st === 'UNPAID' || st === 'DUE') statusClass = "status-badge-pending";

                      const isUnpaid = st === 'UNPAID' || st === 'PENDING' || st === 'DUE';
                      const paidAmountValue = (pkg.paidAmount !== undefined && pkg.paidAmount !== null)
                        ? pkg.paidAmount
                        : (isUnpaid ? 0 : (pkg.totalAmount || 0));

                      const sessionCount = pkg.sessions ?? pkg.noOfSessions ?? pkg.packageSessions ?? pkg.totalSessions ?? pkg.no_of_sessions ?? 1;

                      return (
                        <tr
                          key={idx}
                          className="clickable-table-row"
                          onClick={() => {
                            const apptId = pkg.appointmentId ? getAppointmentId(pkg.appointmentId) : null;
                            if (apptId && apptId !== 'N/A') {
                              window.open(`/outpatient/appointments/patientInfoPanel/${apptId}`, '_blank');
                            }
                          }}
                          title="Click to view package details"
                        >
                          <td><strong>{formatReadableDate(pkg.invoiceTime || pkg.createdDate || pkg.date)}</strong></td>
                          <td>
                            <span className="package-name-badge">
                              <FaBoxOpen size={11} /> {pkg.packageName || pkg.invoiceName || 'Package'}
                            </span>
                          </td>
                          <td>
                            <span className="session-pill">
                              {sessionCount} {sessionCount === 1 ? 'Session' : 'Sessions'}
                            </span>
                          </td>
                          <td style={{ fontWeight: 600 }}>₹{pkg.totalCharge || pkg.totalAmount || pkg.unitPrice || 0}</td>
                          <td style={{ color: '#ea580c', fontWeight: 600 }}>
                            {pkg.discount || pkg.discountAmount ? `₹${pkg.discount || pkg.discountAmount}` : '₹0'}
                          </td>
                          <td className={paidAmountValue > 0 ? "amount-cell" : ""}>
                            ₹{paidAmountValue}
                          </td>
                          <td>
                            <span className={pkg.refundAmount ? "refund-badge" : "muted-dash"}>
                              {pkg.refundAmount ? `₹${pkg.refundAmount}` : '₹0'}
                            </span>
                          </td>
                          <td>
                            <span className={statusClass}>
                              {rawStatus}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state-card">
                <FaBoxOpen size={36} color="#94a3b8" />
                <h4>No Package History Found</h4>
                <p>There are no healthcare or diagnostic package invoices recorded for this patient profile.</p>
              </div>
            )
          )}
        </div>
      </div>

      {/* Edit Patient Modal */}
      <EditPatientModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        patient={currentPatient}
        onPatientUpdated={(updatedData) => {
          setCurrentPatient((prev) => ({ ...prev, ...updatedData }));
        }}
      />

      {/* Fullscreen Photo Lightbox Modal */}
      {showPhotoModal && (
        <div className="photo-lightbox-backdrop" onClick={() => setShowPhotoModal(false)}>
          <div className="photo-lightbox-modal" onClick={(e) => e.stopPropagation()}>
            <button className="photo-lightbox-close" onClick={() => setShowPhotoModal(false)}>&times;</button>
            <img
              src={photoSrc}
              alt={currentPatient.patientName}
              className="photo-lightbox-img"
            />
            <div className="photo-lightbox-caption">
              <strong>{currentPatient.patientName}</strong> ({currentPatient.mrNumber})
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientInfoDisplay;
